/* globals fetch document */
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import mapboxgl from 'mapbox-gl';
import moment from 'moment';

import { DispatchContext } from '../Context';
import { getZ3PConfigValue, pickColor } from '../helper';
import { fetchTraccarVehicleLocations } from '../traccar';
import './GPS.css';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const useStyles = makeStyles((theme) => ({
  mapWrapper: {
    height: '80vh',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      height: 200,
      padding: '3px 0',
    },
  },
  mapContainer: {
    height: '100%',
    width: '100%',
  },
  expandIcon: {
    position: 'absolute',
    zIndex: 2,
    background: '#fff',
    boxShadow: '0 0 0 2px rgba(0,0,0,.1)',
    padding: 2,
    borderRadius: 3,
    cursor: 'pointer',
  },
}));

let map = null;
const currentMarkers = [];
const RouteMap = ({ date }) => {
  const { mapData, setMapData, reOrdered, setReOrdered } = useContext(
    DispatchContext,
  );
  const mapperRef = useRef(null);
  const classes = useStyles();

  // TODO: Maybe delete?
  useEffect(() => {
    if (map) {
      map.resize();
    }
  }, []);

  useEffect(() => {
    // If showing today, load GPS coordinates
    if (moment(date).isSame(moment(), 'day')) {
      fetchTraccarVehicleLocations().then((vehicleLocations) => {
        if (vehicleLocations && vehicleLocations.length) {
          vehicleLocations.forEach((location) => {
            try {
              addVehicleLocationToMap(location);
            } catch (e) {
              console.error('Error adding GPS location to map');
              console.error(e);
            }
          });
        }
      });
    }
  }, []);

  const addVehicleLocationToMap = ({
    serverTime,
    latitude,
    longitude,
    name,
  }) => {
    const diff = moment().diff(moment(serverTime), 'hours', true);
    const lastSeen = moment(serverTime).fromNow();

    // create a DOM element for the marker
    const el = document.createElement('div');
    el.className = diff < 12 ? 'active-gps' : 'old-gps';

    const popup = `<span><h3>${name}</h3><p>Last seen: ${lastSeen}</p></span>`;

    const gpsMarker = new mapboxgl.Marker(el)
      .setLngLat([longitude, latitude])
      .setPopup(new mapboxgl.Popup().setHTML(popup))
      .addTo(map);

    // Show popup on hover instead of click.
    el.addEventListener('mouseenter', () => gpsMarker.togglePopup());
    el.addEventListener('mouseleave', () => gpsMarker.togglePopup());
    // Reverse toggle on click.
    el.onclick = () => {
      gpsMarker.togglePopup();
    };
  };

  useEffect(() => {
    let bounds = null;
    const boundBox = getZ3PConfigValue('lat_lng_bounds');
    if (boundBox?.length === 4) {
      bounds = [
        [boundBox[1], boundBox[0]],
        [boundBox[3], boundBox[2]],
      ];
    }

    if (mapperRef) {
      map = new mapboxgl.Map({
        container: mapperRef.current,
        bounds,
        // style: 'mapbox://styles/zippitymatt/ckeujr2iq0ger19nzirg1wc6h', // Satellite
        // style: 'mapbox://styles/zippitymatt/ckgr1y9tn2xtv19pcmxa6pqcd/draft', // Monochrome-Dispatch
        style: 'mapbox://styles/kpratt/clexgxb45000101mo2bp1rjbu',
      });
      map.addControl(new mapboxgl.FullscreenControl());
      map.addControl(new mapboxgl.NavigationControl());
    }
  }, []);

  const callOsrm = useCallback(
    (mapItems) => {
      mapItems.forEach((data) => {
        const lngLatQuery = data.stops?.reduce((acc, next) => {
          return `${acc}${acc.length ? ';' : ''}${next.lng},${next.lat}`;
        }, '');
        if (lngLatQuery.includes(';')) {
          fetch(
            `https://router.project-osrm.org/route/v1/driving/${lngLatQuery}?overview=full&geometries=geojson`,
          )
            .then((res) => res.json())
            .then((res) => {
              const newMapData = mapData?.map((val) =>
                data.service_vehicle_id === val.service_vehicle_id
                  ? {
                      ...val,
                      coordinates: res.routes[0].geometry.coordinates,
                    }
                  : { ...val },
              );
              setMapData(newMapData);
            });
        }
      });
    },
    [mapData, setMapData],
  );

  useEffect(() => {
    if (mapData?.length > 0 && !reOrdered) {
      callOsrm(mapData.filter((data) => !data.coordinates));
    }
  }, [callOsrm, mapData, reOrdered]);

  useEffect(() => {
    if (mapData?.length > 0 && reOrdered) {
      callOsrm(mapData);
      setReOrdered(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callOsrm, reOrdered, setReOrdered]);

  useEffect(() => {
    if (currentMarkers.length > 0) {
      currentMarkers.forEach((currentMarker) => currentMarker.remove());
      currentMarkers.length = 0;
    }
    if (
      mapData?.filter((data) => data.coordinates && data.selected).length > 0
    ) {
      mapData
        .filter((data) => data.coordinates && data.selected)
        .forEach((data) => {
          data.stops.forEach((datum, i) => {
            const markerColor = pickColor(data.service_vehicle_id);
            let customMarker = null;
            if (i === 0 || i === data.stops.length - 1) {
              customMarker = new mapboxgl.Marker({
                color: markerColor,
                draggable: false,
              })
                .setLngLat([datum.lng, datum.lat])
                .addTo(map);
            } else {
              const el = document.createElement('div');
              const node = document.createTextNode(i);
              el.appendChild(node);
              el.className = 'route-marker';
              el.style.backgroundColor = markerColor;
              customMarker = new mapboxgl.Marker(el, {
                color: markerColor,
                draggable: false,
              })
                .setLngLat([datum.lng, datum.lat])
                .addTo(map);
            }
            currentMarkers.push(customMarker);
          });
        });
    }
  }, [mapData]);

  useEffect(() => {
    if (
      mapData?.filter((data) => data.coordinates && data.selected).length > 0
    ) {
      const stops = mapData
        .filter((data) => data.selected)
        .map((data) => data.stops)
        .flat()
        .filter((x) => x && x.lng && x.lat);
      if (stops.length) {
        const lngs = stops.map((s) => s.lng);
        const lats = stops.map((s) => s.lat);

        map.fitBounds(
          [
            [Math.min(...lngs), Math.min(...lats)],
            [Math.max(...lngs), Math.max(...lats)],
          ],
          { maxZoom: 15, padding: 50 },
        );
      }
    }
  }, [mapData]);

  const loadMapData = () => {
    mapData.forEach(({ service_vehicle_id }) => {
      if (map.getLayer(`route${service_vehicle_id}`)) {
        map.removeLayer(`route${service_vehicle_id}`);
      }
      if (map.getSource(`route${service_vehicle_id}`)) {
        map.removeSource(`route${service_vehicle_id}`);
      }
    });
    mapData
      .filter((data) => data.coordinates && data.selected && data.stops.length)
      .forEach(({ coordinates, service_vehicle_id }) => {
        if (!map.getSource(`route${service_vehicle_id}`)) {
          map.addSource(`route${service_vehicle_id}`, {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates,
              },
            },
          });
        }
        if (
          !map.getLayer(`route${service_vehicle_id}`) &&
          map.getSource(`route${service_vehicle_id}`)
        ) {
          map.addLayer({
            id: `route${service_vehicle_id}`,
            type: 'line',
            source: `route${service_vehicle_id}`,
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': pickColor(service_vehicle_id),
              'line-width': 6,
              'line-opacity': 0.75,
            },
          });
        }
      });
  };

  useEffect(() => {
    if (mapData?.length > 0) {
      if (map?.isStyleLoaded()) {
        loadMapData();
      } else {
        map.on('load', loadMapData);
      }
    }
  }, [mapData]);

  return (
    <>
      <Box className={classes.mapWrapper} position="relative">
        <div ref={mapperRef} className="mapContainer" />
      </Box>
    </>
  );
};

export default RouteMap;
